/**
 * MediaTabPanel — Dedicated media hub for a project.
 *
 * Sections (current and planned):
 *   1. Progress Photos — Chronological photo grid with upload, lightbox, delete
 *   2. Videos (future) — Embedded video links with timecodes
 *   3. Documents (future) — Attached files, PDFs, spreadsheets
 *   4. Photo Packs (future) — Step-by-step photo guides from templates
 *
 * Permissions:
 *   - System admins and content editors can upload/delete
 *   - All project viewers can browse
 */

import React from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { canEditProjectContent } from '../../security/securityAdapter';
import ProgressGallery from '../../Components/ProgressGallery/ProgressGallery';
import styles from './ProjectDetail.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface MediaTabPanelProps {
  /** Project GUID (any case — will be normalized). */
  projectGuid: string;
  /** Current user GUID for permission checks. */
  currentUserGuid: string;
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function MediaTabPanel({
  projectGuid,
  currentUserGuid,
  debugMode,
}: MediaTabPanelProps) {
  const { t } = useAppTranslation('pages');

  const pgNorm = (projectGuid || '').toUpperCase();
  const ugNorm = (currentUserGuid || '').toUpperCase();
  const canUpload = !!(ugNorm && pgNorm && canEditProjectContent(ugNorm, pgNorm));

  return (
    <div className={styles.panel}>
      {/* ============================================================ */}
      {/*  Section 1: Progress Photos                                   */}
      {/* ============================================================ */}
      {pgNorm ? (
        <ProgressGallery
          entityType="project"
          entityGuid={pgNorm}
          canUpload={canUpload}
          debugMode={debugMode}
        />
      ) : (
        <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
          {t('media.noProject')}
        </div>
      )}

      {/* ============================================================ */}
      {/*  Section 2: Videos (future)                                   */}
      {/* ============================================================ */}
      {/* Placeholder for video embeds with timecodes */}

      {/* ============================================================ */}
      {/*  Section 3: Documents (future)                                */}
      {/* ============================================================ */}
      {/* Placeholder for attached documents and files */}
    </div>
  );
}
